import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import { GridColDef } from "@mui/x-data-grid-premium";

import { useTableSettings } from "src/hooks/table/tableSettings/useTableSettings";
import { CustomDataGrid } from "src/components/organisms/CustomDataGrid";
import CustomButton from "src/components/atoms/buttons/CustomButton";

/* ────────── helpers ────────── */
const flattenKeys = (obj: any, prefix = ""): string[] => {
  if (obj == null || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj).flatMap(([k, v]) => {
    const nk = prefix ? `${prefix}_${k}` : k;
    return typeof v === "object" && v !== null && !Array.isArray(v)
      ? flattenKeys(v, nk)
      : [nk];
  });
};

const getValueFromFlattenedKey = (obj: any, flatKey: string) =>
  flatKey.split("_").reduce((acc, k) => (acc ? acc[k] : undefined), obj);

/* ────────── component ────────── */
interface Props {
  /** usually Formik’s initialValues */
  initialValues: Record<string, any>;
  /** CSS selector that returns the form element (defaults to "form") */
  formSelector?: string;
  /** Dialog title */
  title?: string;
}

export const FormInspectionButton: React.FC<Props> = ({
  initialValues,
  formSelector = "form",
  title = "Form / API field inspection",
}) => {
  const [open, setOpen] = useState(false);

  /* 1. collect field names rendered in the DOM */
  /* 1. collect field names rendered in the DOM */
const domFieldNames = useMemo(() => {
    if (!open) return new Set<string>();        // scan only when dialog is open
  
    // ❶ pick a root to search inside
    const root: HTMLElement | null =
      document.querySelector(formSelector)      // user-supplied selector
      ?? document.querySelector("form")         // fallback: first <form>
      ?? document.body;                         // ultimate fallback
  
    // ❷ everything that carries a “name” (real inputs, hidden
    //    Formik <Field> helpers, MUI nativeInput, checkboxes, etc.)
    const nodes = Array.from(
      root.querySelectorAll<HTMLElement>("[name]"),
    );
  
    // ❸ ignore items created by MUI Select / Autocomplete that are empty
    const names = nodes
      .map((el) => el.getAttribute("name")!.trim())
      .filter(Boolean)                          // strip "" / null
      .filter((n) => !n.startsWith("mui-"));    // ignore MUI internal helpers
  
    return new Set(names);
  }, [open, formSelector]);
  

  /* 2. build rows */
  const inspectionRows = useMemo(() => {
    const apiFields = new Set(flattenKeys(initialValues));
    const all = new Set([...apiFields, ...domFieldNames]);

    let id = 0;
    return Array.from(all).map((field) => {
      const sample = getValueFromFlattenedKey(initialValues, field);
      return {
        id: id++,
        field,
        inApi: apiFields.has(field),
        inForm: domFieldNames.has(field),
        status: apiFields.has(field) && domFieldNames.has(field)
          ? "Both"
          : apiFields.has(field)
          ? "API only"
          : "Form only",
        sample:
          sample !== null && sample !== undefined && sample !== "" ? sample : null,
      };
    });
  }, [initialValues, domFieldNames]);

  /* 3. grid columns */
  const inspectionCols: GridColDef[] = [
    { field: "field", headerName: "Field name", width: 240 },
    { field: "inApi", headerName: "In API", type: "boolean", width: 120 },
    { field: "inForm", headerName: "In Form", type: "boolean", width: 120 },
    { field: "status", headerName: "Status", width: 160 },
    {
      field: "sample",
      headerName: "Sample value",
      flex: 1,
      minWidth: 200,
      renderCell: (params) =>
        params.value === null ? (
          <span style={{ color: "red", fontWeight: "bold" }}>EMPTY</span>
        ) : (
          <span>{String(params.value)}</span>
        ),
    },
  ];

  /* 4. tableSettings for the dialog’s grid */
  const inspectionSettings = useTableSettings({
    columns: inspectionCols,
    rows: inspectionRows,
    tableName: "FormInspection",
    hasToolbar: false,
    hideFooter: true,
    checkboxSelection: false,
    idPath: ["id"],
  });
  inspectionSettings.loading = false;

  /* 5. UI */
  return (
    <>
      <CustomButton variant="text" onClick={() => setOpen(true)} title="Inspect" icon={<QueryStatsRoundedIcon />} />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { width: "90vw", height: "90vh" } }}
        scroll="paper"
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <CustomDataGrid
            tableSettings={inspectionSettings}
            columns={inspectionCols}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
